import { AnalyticsService } from '@core/services';
import { ContactContent } from '@core/services/site-content/site-content.types';
import { Inject } from '@rxdi/core';
import { Component, css, html, LitElement, property } from '@rxdi/lit-html';
import { rich } from '@shared/rich-text';
import { ArrowIcon, StudioStyles } from '@shared/styles/studio.styles';

/** Home: the closing call to action. */
@Component({
  selector: 'rs-contact',
  styles: [
    StudioStyles,
    css`
      .panel {
        position: relative;
        overflow: hidden;
        padding: clamp(32px, 6vw, 88px);
        border-radius: calc(var(--rs-radius-lg) + 8px);
        border: 1px solid var(--rs-line-strong);
        background: radial-gradient(60% 80% at 100% 0%, var(--rs-plasma-soft), transparent 60%),
          radial-gradient(50% 70% at 0% 100%, var(--rs-signal-soft), transparent 60%), var(--rs-surface);
        isolation: isolate;
      }

      .panel .grid-bg {
        z-index: -1;
      }

      .big {
        font-family: var(--rs-font-display);
        font-weight: 700;
        font-size: clamp(44px, 8vw, 116px);
        letter-spacing: -0.04em;
        line-height: 0.95;
        margin: 22px 0 0;
        max-width: 12ch;
        text-wrap: balance;
      }

      .big em {
        font-style: normal;
        background: var(--rs-gradient-brand);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }

      .cols {
        display: grid;
        gap: 40px;
        margin-top: clamp(36px, 5vw, 64px);
      }

      @media (min-width: 960px) {
        .cols {
          grid-template-columns: 1.2fr 1fr;
          align-items: end;
        }
      }

      .mail {
        display: inline-flex;
        align-items: center;
        gap: 14px;
        font-family: var(--rs-font-display);
        font-size: clamp(22px, 3vw, 36px);
        letter-spacing: -0.02em;
        border-bottom: 2px solid var(--rs-signal);
        padding-bottom: 6px;
        width: fit-content;
        word-break: break-all;
      }

      .mail svg {
        width: 22px;
        height: 22px;
        transition: transform 0.3s var(--rs-ease);
      }

      .mail:hover svg {
        transform: translate(4px, -4px) rotate(-45deg);
      }

      .lines {
        display: flex;
        gap: 24px;
        flex-wrap: wrap;
        margin-top: 22px;
        font-family: var(--rs-font-mono);
        font-size: 14px;
        color: var(--rs-text-2);
      }

      .lines a:hover {
        color: var(--rs-signal);
      }

      ol {
        list-style: none;
        margin: 18px 0 0;
        padding: 0;
        display: grid;
        gap: 14px;
        counter-reset: step;
      }

      li {
        display: grid;
        grid-template-columns: 36px 1fr;
        gap: 12px;
        color: var(--rs-text-2);
        line-height: 1.55;
        counter-increment: step;
      }

      li::before {
        content: '0' counter(step);
        font-family: var(--rs-font-mono);
        font-size: 12px;
        color: var(--rs-signal);
        padding-top: 3px;
      }

      .ctas {
        margin-top: 28px;
      }
    `,
  ],
  template(this: ContactComponent) {
    const c = this.content;
    if (!c) {
      return html``;
    }
    const phoneHref = `tel:${c.phone.replace(/[^+\d]/g, '')}`;
    return html`
      <section class="section" id="contact">
        <div class="wrap">
          <div class="panel">
            <div class="grid-bg" aria-hidden="true"></div>
            <span class="kicker"><span class="index">07</span> Start a project</span>
            <h2 class="big">${rich(c.heading)}</h2>

            <div class="cols">
              <div>
                <a
                  class="mail"
                  href="mailto:${c.email}?subject=New%20project"
                  @click=${() => this.analytics.track('contact_click', { type: 'email', action: 'click' })}
                >
                  ${c.email} ${ArrowIcon}
                </a>
                <div class="lines">
                  <a
                    href=${phoneHref}
                    @click=${() => this.analytics.track('contact_click', { type: 'phone', action: 'click' })}
                    >${c.phone}</a
                  >
                  <span>${c.city}</span>
                </div>
                <div class="ctas">
                  <a
                    class="btn btn-signal"
                    href="mailto:${c.email}?subject=New%20project"
                    @click=${() => this.analytics.track('contact_click', { type: 'email', action: 'cta' })}
                  >
                    Write to us ${ArrowIcon}
                  </a>
                </div>
              </div>
              <div>
                <span class="kicker">What happens next</span>
                <ol>
                  ${c.next.map((n) => html`<li>${n}</li>`)}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>
    `;
  },
})
export class ContactComponent extends LitElement {
  @property({ attribute: false }) content: ContactContent;

  @Inject(AnalyticsService)
  private analytics: AnalyticsService;
}
